import { ReactNode } from 'react';
import { Location } from 'react-router-dom';

import Pagination from '@/components/Pagination/hammer-pagination/Pagination';
import { PaginationFilter } from '@/types/PaginationFilter';

import { Folder } from '../Folders/types/Folder';
import ConversationsList from './components/ConversationsList';
import { NoConversations } from './components/NoConversations';
import { ConversationType } from './types/Conversation';
import { FilterLabel } from './types/Label';

const PAGE_SIZE_OPTIONS = [48, 72, 96, 120];

export type ConversationsProps = {
    /** Index of the currently active folder in `folders` */
    readonly activeFolder: number | null;
    /** Copy for "Archive" button */
    readonly archiveLabel: ReactNode;
    /** List of conversations in currently selected folder */
    readonly conversations: ConversationType[];
    /** Indicates conversations are loading for currently selected folder */
    readonly conversationsLoading: boolean;
    /** active filter */
    readonly filter: FilterLabel;
    /** List of folders for the current user */
    readonly folders: Folder[];
    /** function for formatting numbers */
    readonly formatNumber: (num: number) => string;
    /** Callback which returns the url for a conversation */
    readonly getMessengerUrl: (conversationId: number) => string;
    /** Current router location */
    readonly location: Location;
    /** Copy for "Mark Read" button */
    readonly markReadLabel: ReactNode;
    /** Copy for "Mark Unread" button */
    readonly markUnreadLabel: ReactNode;
    /** Toggles mobile responsive design */
    readonly mobile?: boolean;
    /** Copy for "Move" button */
    readonly moveLabel: ReactNode;
    /** Copy for "No conversations message" in paginator */
    readonly noConversationsMessage?: ReactNode;
    /** Callback function for archiving conversation */
    readonly onArchive: (conversationIds: number[]) => void;
    /** Callback function called when changing the pagination of conversations */
    readonly onChangePagination: (paginationFilter: PaginationFilter) => void;
    /** Callback function to change the view filter 'read', 'unread', 'all' */
    readonly onFilter: (filter: FilterLabel) => void;
    /** Callback function to mark a conversation as read */
    readonly onMarkRead: (conversationIds: number[]) => void;
    /** Callback function to mark a conversation as unread */
    readonly onMarkUnread: (conversationIds: number[]) => void;
    /** Callback function to move a conversation to a different folder */
    readonly onMove: (conversationIds: number[], folder: Folder) => void;
    /** Filter params passed to paginator */
    readonly paginationFilter: PaginationFilter;
};

export const Conversations = ({
    activeFolder,
    archiveLabel,
    conversations,
    conversationsLoading = false,
    filter,
    folders,
    formatNumber,
    getMessengerUrl,
    location,
    markReadLabel,
    markUnreadLabel,
    mobile,
    moveLabel,
    noConversationsMessage,
    onArchive,
    onChangePagination,
    onFilter,
    onMarkRead,
    onMarkUnread,
    onMove,
    paginationFilter,
}: ConversationsProps) => {
    const hasConversationsInCurrentFolder = Boolean(conversations.length);
    return (
        <div className="block flex-auto">
            <ConversationsList
                activeFolder={activeFolder}
                archiveLabel={archiveLabel}
                className="flex-auto"
                conversations={conversations}
                filter={filter}
                folders={folders}
                formatNumber={formatNumber}
                getMessengerUrl={getMessengerUrl}
                markReadLabel={markReadLabel}
                markUnreadLabel={markUnreadLabel}
                mobile={mobile}
                moveLabel={moveLabel}
                onArchive={onArchive}
                onFilter={onFilter}
                onMarkRead={onMarkRead}
                onMarkUnread={onMarkUnread}
                onMove={onMove}
                paginationFilter={paginationFilter}
            />
            {hasConversationsInCurrentFolder ? (
                <Pagination
                    canonicalUrl={`${location?.pathname}`}
                    onChange={onChangePagination}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    paginationFilter={paginationFilter}
                />
            ) : !conversationsLoading ? (
                <NoConversations message={noConversationsMessage} />
            ) : null}
        </div>
    );
};
